import { h } from "preact";
import { Suspense, lazy } from "preact/compat";
import { useContext, useMemo } from "preact/hooks";
import { MediaPlayerProps } from "./types/playerTypes";
import SettingsContext from "../SettingsContext";
import { AUDIO_EXTENSIONS } from "./helpers/patterns";
import { useRecordedURL } from "../helpers/routes";
import ADPlayer from "../AudioDescription/components/ADPlayer";

const PADDING = 12;

const YouTubePlayer = lazy(() => import("./vendors/YouTube/YouTube"));
const BrightcovePlayer = lazy(() => import("./vendors/Brightcove/Brightcove"));
const KalturaPlayer = lazy(() => import("./vendors/Kaltura/Kaltura"));
const WistiaPlayer = lazy(() => import("./vendors/Wistia/Wistia"));
const WistiaAuroraPlayer = lazy(
  () => import("./vendors/WistiaAurora/WistiaAurora")
);
const NativePlayer = lazy(() => import("./vendors/Native/Native"));

type SupportedPlatform =
  | "native"
  | "YouTube"
  | "Brightcove"
  | "Kaltura"
  | "Wistia"
  | "WistiaAurora";

function isSupportedPlatform(platform: string): platform is SupportedPlatform {
  return [
    "native",
    "YouTube",
    "Brightcove",
    "Kaltura",
    "Wistia",
    "WistiaAurora",
  ].includes(platform);
}

function MediaPlayer(props: MediaPlayerProps) {
  const { width, height } = props;
  const { settings } = useContext(SettingsContext);

  const threeplayHostedURL = useRecordedURL("/source");
  const videoUrl = useMemo(() => {
    if (settings.platform === "native") {
      return settings.playback ?? threeplayHostedURL.toString();
    } else if (settings.platform === "YouTube") {
      return `https://www.youtube-nocookie.com/embed/${settings.mediaId}?enablejsapi=1`;
    }
  }, [
    settings.platform,
    settings.playback,
    threeplayHostedURL,
    settings.mediaId,
  ]);

  const style = useMemo(() => {
    const usingAudioPlayer =
      settings.platform === "native" &&
      (settings.playAs === "audio" || AUDIO_EXTENSIONS.test(videoUrl || ""));

    if (usingAudioPlayer) {
      return {
        "padding-bottom": `${getAudioElementHeight() + 2 * PADDING}px`,
      };
    }

    // This needs to match the aspect ratio used by the video provider
    switch (settings.platform) {
      case "Wistia":
      case "WistiaAurora":
        return {
          "padding-bottom": settings.aspect
            ? `${100 / settings.aspect}%`
            : "75%",
        };

      default:
        return {
          "padding-bottom": settings.aspect
            ? `${100 / settings.aspect}%`
            : "56.25%",
        };
    }
  }, [settings.platform, settings.playAs, settings.aspect, videoUrl]);

  if (!isSupportedPlatform(settings.platform)) return null;

  return (
    <div style={{ width, height, ...style }} class="MediaPlayer">
      <Suspense fallback={<div style={{ padding: "6px" }}>Loading...</div>}>
        <Player
          platform={settings.platform}
          {...props}
          {...(videoUrl && { url: videoUrl })}
          mediaId={settings.mediaId || ""}
        />
        {props.hasAD && (
          <ADPlayer isAdEnabled={props.isAdEnabled} extended={props.extended} />
        )}
      </Suspense>
    </div>
  );
}

function Player({
  platform,
  ...props
}: { platform: SupportedPlatform } & MediaPlayerProps) {
  switch (platform) {
    case "native":
      return <NativePlayer {...props} />;
    case "YouTube":
      return <YouTubePlayer />;
    case "Brightcove":
      return <BrightcovePlayer />;
    case "Kaltura":
      return <KalturaPlayer />;
    case "Wistia":
      return <WistiaPlayer />;
    case "WistiaAurora":
      return <WistiaAuroraPlayer />;
  }
}

/**
 * Cross-browser way to get the audio element height by attaching one
 * to the body and measuring it.
 */
function getAudioElementHeight() {
  const a = document.createElement("audio");
  a.setAttribute("src", "");
  a.setAttribute("controls", "true");
  a.style.visibility = "hidden";
  a.style.position = "fixed";
  a.style.top = "-100%";
  a.style.left = "-100%";
  document.body.appendChild(a);

  const { height } = a.getBoundingClientRect();
  a.remove();

  return height;
}

export default MediaPlayer;
